import { Injectable, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConversationApiMessage, ConversationListApiItem, ConversationListApiService } from './conversation-list-api.service';
import { RealtimeStompService } from './realtime-stomp.service';
import { getChatbotUrl, getWebSocketUrl } from './runtime-config';
import { AuthService } from './auth.service';
import {
  ContactPreview,
  Conversation,
  Message,
  MessageStatus,
  MessageType
} from './models';

function safeDate(ts: string): Date {
  const raw = String(ts ?? '').trim();
  const asNum = Number(raw);
  if (!Number.isNaN(asNum)) {
    const millis = raw.length <= 10 ? asNum * 1000 : asNum;
    const fromEpoch = new Date(millis);
    if (!isNaN(fromEpoch.getTime())) return fromEpoch;
  }
  const d = new Date(raw);
  return isNaN(d.getTime()) ? new Date() : d;
}

function formatHHMM(ts: string): string {
  const d = safeDate(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function nextId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9).toUpperCase()}`;
}

const AGENT_PHONE = '+919876543210';
const WS_URL = getWebSocketUrl();
const DASHBOARD_SEND_ENDPOINT = `${getChatbotUrl()}/api/dashboard/send`;
const DASHBOARD_HUMAN_COMPLETE_ENDPOINT = `${getChatbotUrl()}/api/dashboard/human/complete`;
const BILLING_WINDOW_MS = 24 * 60 * 60 * 1000;
type InboxQuickFilter = 'All' | 'HumanRequired';
type ComposerSubmitPayload = { text: string; file?: File };
type SeededContactMeta = Pick<
  ContactPreview,
  'mainCategory' | 'subCategory' | 'stage' | 'orderValue' | 'orderCurrency' | 'orderStatus'
>;

const SALES_SUB_CATEGORIES = ['Starter Plan', 'Growth Plan', 'Pro Plan', 'Enterprise Plan'] as const;
const SALES_STAGES = ['Order Placed', 'Order Confirmed', 'Payment Received', 'Fulfilled', 'Rejected'] as const;

function mapMessageStatus(status: string): MessageStatus {
  const normalized = status.toUpperCase();
  if (normalized === 'SENDING') return 'sending';
  if (normalized === 'FAILED') return 'failed';
  if (normalized === 'SENT') return 'sent';
  if (normalized === 'DELIVERED') return 'delivered';
  if (normalized === 'READ') return 'read';
  return 'delivered';
}

function inferMessageTypeFromText(content: string): MessageType {
  const normalized = content.trim().toUpperCase();
  if (normalized === 'IMAGE') return 'image';
  if (normalized === 'VIDEO') return 'video';
  if (normalized === 'AUDIO') return 'audio';
  if (normalized === 'DOCUMENT' || normalized === 'DOC') return 'document';
  return 'text';
}

function inferMessageTypeFromApiMessage(m: ConversationListApiItem['messages'][number]): MessageType {
  const raw = (m.messageType || '').toUpperCase();
  const interactive = m.interactive as unknown as {
    type?: string;
    nfm_reply?: unknown;
  } | undefined;
  const interactiveType = (interactive?.type || '').toLowerCase();
  if (raw === 'TEXT') return 'text';
  if (raw === 'IMAGE') return 'image';
  if (raw === 'VIDEO') return 'video';
  if (raw === 'AUDIO') return 'audio';
  if (raw === 'DOCUMENT') return 'document';
  if (raw.includes('INTERACTIVE_FLOW_SENT') || raw.includes('FLOW_SENT') || interactiveType === 'flow') return 'flow';
  if (interactiveType === 'nfm_reply' || !!interactive?.nfm_reply || raw.includes('FLOW_REPLY')) return 'flow_response';
  if (raw.includes('INTERACTIVE_BUTTON_REPLY') || raw.includes('INTERACTIVE_LIST_REPLY')) return 'interactive_reply';
  if (raw.includes('INTERACTIVE') || m.interactive?.type === 'buttons') return 'interactive';
  if (raw.includes('FLOW')) return 'flow';
  if (m.media?.type) return inferMessageTypeFromText(m.media.type);
  return inferMessageTypeFromText(m.text || m.content || '');
}

function getMessageText(m: ConversationListApiItem['messages'][number]): string {
  if (m.text?.trim()) return m.text;

  const rawType = (m.messageType || '').toUpperCase();
  if (rawType === 'INTERACTIVE_BUTTON_SENT') {
    const interactive = m.interactive as unknown as {
      type?: string;
      buttons?: Array<{ title?: string; reply?: { title?: string } }>;
      text?: string;
      body?: { text?: string };
      header?: { text?: string };
    } | undefined;
    const isButtons = interactive?.type === 'buttons' || !!interactive?.buttons?.length;
    if (isButtons) {
      const interactiveText = interactive?.text?.trim()
        || interactive?.body?.text?.trim()
        || interactive?.header?.text?.trim()
        || '';
      if (interactiveText) return interactiveText;
      if (m.content?.trim()) return m.content;
      return '';
    }
    return '';
  }

  if (m.content?.trim()) return m.content;
  if (m.interactive?.title?.trim()) return m.interactive.title;
  const altInteractive = m.interactive as unknown as { button_reply?: { title?: string }, text?: string } | undefined;
  if (altInteractive?.button_reply?.title?.trim()) return altInteractive.button_reply.title;
  if (altInteractive?.text?.trim()) return altInteractive.text;
  if (m.media?.type?.trim()) return m.media.type.toUpperCase();
  if (m.messageType?.trim()) return m.messageType.replaceAll('_', ' ');
  return '';
}

function getInteractiveData(m: ConversationListApiItem['messages'][number]): Message['interactive'] {
  const interactive = m.interactive as unknown as {
    type?: string;
    buttons?: Array<{
      id?: string;
      title?: string;
      text?: string;
      reply?: { id?: string; title?: string };
      type?: string;
    }>;
    footer?: { text?: string };
    header?: { text?: string };
    body?: { text?: string };
    action?: {
      buttons?: Array<{ id: string; title?: string; text?: string }>;
      parameters?: { flow_cta?: string };
    };
    nfm_reply?: { body?: string; name?: string };
    button_reply?: { id?: string; title?: string };
    id?: string;
    title?: string;
  } | undefined;
  if (!interactive) return undefined;

  const directButtons = interactive.buttons || [];
  const actionButtons = interactive.action?.buttons || [];
  const normalizedButtons = [...directButtons, ...actionButtons]
    .map((b, idx) => {
      const btn = b as {
        id?: string;
        title?: string;
        text?: string;
        reply?: { id?: string; title?: string };
      };
      return {
        id: btn.reply?.id || btn.id || `btn_${idx}`,
        title: btn.reply?.title || btn.title || btn.text || '',
      };
    })
    .filter(b => !!b.title);

  return {
    type: interactive.type,
    buttons: normalizedButtons.length ? normalizedButtons : undefined,
    id: interactive.id || interactive.button_reply?.id,
    title: interactive.title || interactive.button_reply?.title,
    headerText: interactive.header?.text,
    bodyText: interactive.body?.text,
    footerText: interactive.footer?.text,
    ctaText: interactive.action?.parameters?.flow_cta,
    responseBody: interactive.nfm_reply?.body || interactive.nfm_reply?.name,
  };
}

function getMediaData(m: ConversationListApiItem['messages'][number]): Message['media'] {
  const media = m.media as unknown as {
    type?: string;
    url?: string;
    link?: string;
  } | undefined;
  const withAlt = m as unknown as {
    mediaUrl?: string;
    url?: string;
    video?: { url?: string };
    image?: { url?: string };
    document?: { url?: string };
  };

  const rawUrl = media?.url
    || media?.link
    || withAlt.mediaUrl
    || withAlt.video?.url
    || withAlt.image?.url
    || withAlt.document?.url
    || withAlt.url;
  const url = rawUrl?.trim();

  const type = media?.type || (m.messageType || '').toLowerCase();
  if (!url && !type) return undefined;
  return { type, url };
}

function normalizeTimestamp(ts: string): string {
  return safeDate(ts).toISOString();
}

function seededMetaForContact(convId: string, index: number): SeededContactMeta {
  // Keep this intentionally minimal: seed only a few Sales orders for local UI testing.
  if (index > 3) return {};
  const hash = [...convId].reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const subCategory = SALES_SUB_CATEGORIES[hash % SALES_SUB_CATEGORIES.length];
  const stage = SALES_STAGES[hash % SALES_STAGES.length];
  const orderValue = 150 + ((hash * 23) % 2400);
  const paid = stage === 'Payment Received' || stage === 'Fulfilled';
  return {
    mainCategory: 'Sales',
    subCategory,
    stage,
    orderValue,
    orderCurrency: 'USD',
    orderStatus: paid ? 'paid' : 'open',
  };
}

@Injectable({ providedIn: 'root' })
export class ChatStoreService {
  private apiContactsSig = signal<ConversationListApiItem[]>([]);
  private liveUnsubscribeByConv = new Map<string, () => void>();
  private readonly fallbackSyncIntervalMs = 8000;
  private fallbackSyncTimer: ReturnType<typeof setInterval> | null = null;
  private fallbackSyncInFlight = false;

  selectedConversationId = signal<string>('');
  search = signal<string>('');
  activeLabelFilter = signal<InboxQuickFilter>('All');

  constructor(
    private readonly http: HttpClient,
    private readonly contactApi: ConversationListApiService,
    private readonly realtime: RealtimeStompService,
    private readonly auth: AuthService
  ) {
    void this.loadContactLabels();
    this.startFallbackSync();
  }

  conversation = computed<Conversation | null>(() => {
    const contact = this.selectedContact();
    if (!contact) return null;

    return {
      orgId: 'API_SOURCE',
      conversationId: contact.conversationId,
      name: contact.displayName,
      from: contact.phone,
      to: AGENT_PHONE,
      messages: this.messages(),
    };
  });

  allContacts = computed<ContactPreview[]>(() => {
    const nowTs = new Date().toISOString();

    return this.apiContactsSig()
      .map((row, index): ContactPreview => {
        const seededMeta = seededMetaForContact(row.convId, index);
        const handledBy = (row.handledBy || '').toLowerCase() === 'human' ? 'human' : 'bot';
        const requiresHuman = row.agentRequired ?? (((row.handledBy || '').toLowerCase() === 'human') || !!row.humanAgent);
        return {
          conversationId: row.convId,
          displayName: row.contactLabel?.trim() || row.phoneNumber || 'Unknown',
          phone: row.phoneNumber || 'Unknown',
          lastMessage: row.lastMessage || '',
          lastMessageType: inferMessageTypeFromText(row.lastMessage || ''),
          messageTs: row.lastMessageTs ? normalizeTimestamp(row.lastMessageTs) : nowTs,
          botEnabled: handledBy === 'bot',
          billableOnNextCustomerMessage: (Date.now() - safeDate(row.lastMessageTs).getTime()) > BILLING_WINDOW_MS,
          label: row.status || undefined,
          handledBy,
          requiresHumanAgent: !!requiresHuman,
          humanAgent: !!requiresHuman,
          mainCategory: seededMeta.mainCategory,
          subCategory: seededMeta.subCategory,
          stage: seededMeta.stage,
          orderValue: seededMeta.orderValue,
          orderCurrency: seededMeta.orderCurrency,
          orderStatus: seededMeta.orderStatus,
        };
      })
      .sort((a, b) => safeDate(b.messageTs).getTime() - safeDate(a.messageTs).getTime());
  });

  quickFilters = computed(() => {
    const list = this.allContacts();
    return [
      { key: 'All' as InboxQuickFilter, label: 'All', count: list.length },
      {
        key: 'HumanRequired' as InboxQuickFilter,
        label: 'Human Required',
        count: list.filter(c => c.requiresHumanAgent).length,
      },
    ];
  });

  contacts = computed<ContactPreview[]>(() => {
    const q = this.search().trim().toLowerCase();
    const active = this.activeLabelFilter();

    return this.allContacts().filter(item => {
      const byLabelFilter = active === 'All'
        || (active === 'HumanRequired' && !!item.requiresHumanAgent);
      if (!byLabelFilter) return false;
      if (!q) return true;
      const labelQ = (item.label || '').toLowerCase();
      const phoneQ = item.phone.toLowerCase();
      const nameQ = item.displayName.toLowerCase();
      return phoneQ.includes(q) || nameQ.includes(q) || labelQ.includes(q);
    });
  });

  selectedContact = computed<ContactPreview | null>(() => {
    const id = this.selectedConversationId();
    return this.allContacts().find(c => c.conversationId === id) || null;
  });

  messages = computed<Message[]>(() => {
    const contact = this.selectedContact();
    if (!contact) return [];

    const apiRow = this.apiContactsSig().find(row => row.convId === contact.conversationId);
    if (!apiRow) return [];

    return (apiRow.messages || [])
      .map((m, idx): Message => {
        const type = inferMessageTypeFromApiMessage(m);
        const rawType = (m.messageType || '').toUpperCase();
        const forcedInteractiveText = rawType === 'INTERACTIVE_BUTTON_SENT'
          ? (m.text?.trim() || m.content?.trim() || '')
          : '';

        return {
          id: `${contact.conversationId}_${idx}_${m.timestamp}`,
          from: m.direction.toUpperCase() === 'OUTBOUND' ? AGENT_PHONE : contact.phone,
          to: m.direction.toUpperCase() === 'OUTBOUND' ? contact.phone : AGENT_PHONE,
          type,
          content: forcedInteractiveText || getMessageText(m),
          status: mapMessageStatus(m.status || ''),
          timestamp: m.timestamp ? normalizeTimestamp(m.timestamp) : contact.messageTs,
          rawType: m.messageType || undefined,
          interactive: getInteractiveData(m),
          media: getMediaData(m),
        };
      })
      .sort((a, b) => safeDate(a.timestamp).getTime() - safeDate(b.timestamp).getTime());
  });

  async sendMessage(payload: ComposerSubmitPayload): Promise<void> {
    const content = payload.text.trim();
    const file = payload.file;
    if (!content && !file) return;

    const id = this.selectedConversationId();
    const contact = this.selectedContact();
    if (!id || !contact) return;

    const timestamp = new Date().toISOString();
    const localMessageId = nextId('MSG');
    const localFileUrl = file ? URL.createObjectURL(file) : undefined;
    const outgoingType: Message['type'] = file ? this.messageTypeFromFile(file) : 'text';
    const displayContent = content || file?.name || '';

    this.apiContactsSig.update(rows => rows.map(row =>
      row.convId === id
        ? {
            ...row,
            handledBy: 'human',
            agentRequired: true,
            humanAgent: true,
            messages: [
              ...(row.messages || []),
              {
                direction: 'OUTBOUND',
                messageType: this.apiMessageTypeFromUiType(outgoingType),
                text: displayContent,
                timestamp,
                status: 'SENDING',
                media: file ? { type: outgoingType, url: localFileUrl } : undefined,
                localMessageId,
              },
            ],
            lastMessage: displayContent || this.previewLabelForType(outgoingType),
            lastMessageTs: timestamp,
            lastMessageStatus: 'SENT',
          }
        : row
    ));

    const formData = new FormData();
    formData.append('from', this.auth.getOrgId());
    formData.append('to', contact.phone);
    formData.append('messageBody', content);
    if (file) {
      formData.append('file', file);
    }

    try {
      const token = this.auth.getAuthToken();
      const requestOptions = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : undefined;
      await firstValueFrom(this.http.post(DASHBOARD_SEND_ENDPOINT, formData, requestOptions));
    } catch (error) {
      console.error('Failed to send message to dashboard API', error);
      this.updateLocalOutgoingMessageStatus(id, localMessageId, 'FAILED');
      return;
    }
    this.updateLocalOutgoingMessageStatus(id, localMessageId, 'SENT');
  }

  setActiveLabelFilter(filter: InboxQuickFilter): void {
    this.activeLabelFilter.set(filter);
  }

  setBotEnabled(conversationId: string, enabled: boolean): void {
    this.apiContactsSig.update(rows => rows.map(row =>
      row.convId === conversationId
        ? { ...row, handledBy: enabled ? 'bot' : 'human', agentRequired: !enabled, humanAgent: !enabled }
        : row
    ));
  }

  setHumanRequired(conversationId: string, required: boolean): void {
    this.apiContactsSig.update(rows => rows.map(row =>
      row.convId === conversationId
        ? { ...row, handledBy: required ? 'human' : 'bot', agentRequired: required, humanAgent: required }
        : row
    ));
  }

  async enableBotViaDashboard(conversationId: string): Promise<boolean> {
    const target = this.apiContactsSig().find(row => row.convId === conversationId);
    if (!target) return false;

    const payload = {
      from: this.auth.getOrgId(),
      to: String(target.phoneNumber || '').replace(/\D/g, ''),
    };

    try {
      const token = this.auth.getAuthToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await firstValueFrom(
        this.http.post(DASHBOARD_HUMAN_COMPLETE_ENDPOINT, payload, { headers, responseType: 'text' })
      );
      this.setBotEnabled(conversationId, true);
      return true;
    } catch (error) {
      console.error('Failed to complete human handover', error);
      return false;
    }
  }

  hhmm(ts: string): string { return formatHHMM(ts); }

  statusIcon(s: MessageStatus): string {
    if (s === 'sending') return '◷';
    if (s === 'failed') return '!';
    if (s === 'sent') return '✓';
    if (s === 'delivered') return '✓✓';
    return '✓✓';
  }

  private updateLocalOutgoingMessageStatus(conversationId: string, localMessageId: string, status: 'SENT' | 'FAILED'): void {
    this.apiContactsSig.update(rows => rows.map(row => {
      if (row.convId !== conversationId) return row;

      const updatedMessages = (row.messages || []).map((m: ConversationApiMessage & { localMessageId?: string }) =>
        m.localMessageId === localMessageId ? { ...m, status } : m
      );

      return {
        ...row,
        messages: updatedMessages,
      };
    }));
  }

  private messageTypeFromFile(file: File): Message['type'] {
    const mime = file.type.toLowerCase();
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    return 'document';
  }

  private apiMessageTypeFromUiType(type: Message['type']): string {
    if (type === 'text') return 'TEXT';
    if (type === 'image') return 'IMAGE';
    if (type === 'video') return 'VIDEO';
    if (type === 'audio') return 'AUDIO';
    if (type === 'document') return 'DOCUMENT';
    return 'TEXT';
  }

  private previewLabelForType(type: Message['type']): string {
    if (type === 'image') return 'IMAGE';
    if (type === 'video') return 'VIDEO';
    if (type === 'audio') return 'AUDIO';
    if (type === 'document') return 'DOCUMENT';
    return '';
  }

  private async loadContactLabels(): Promise<void> {
    try {
      const rows = await this.contactApi.listContacts();
      this.apiContactsSig.set(rows);
      this.bindRealtimeSubscriptions(rows);

      const selectedId = this.selectedConversationId();
      const selectedStillExists = rows.some(row => row.convId === selectedId);
      if (!selectedId || !selectedStillExists) {
        this.selectedConversationId.set(rows[0]?.convId || '');
      }
    } catch (error) {
      console.error('Failed to load contact labels from API', error);
    }
  }

  private startFallbackSync(): void {
    if (this.fallbackSyncTimer) return;
    this.fallbackSyncTimer = setInterval(() => {
      void this.syncContactsFromApi();
    }, this.fallbackSyncIntervalMs);
    console.log('[ChatStore] started fallback contact sync', {
      intervalMs: this.fallbackSyncIntervalMs,
    });
  }

  private async syncContactsFromApi(): Promise<void> {
    if (this.fallbackSyncInFlight) return;
    this.fallbackSyncInFlight = true;
    try {
      const rows = await this.contactApi.listContacts();
      const current = this.apiContactsSig();
      if (this.areContactsEquivalent(current, rows)) return;

      console.log('[ChatStore] fallback sync detected contact/message changes', {
        previous: current.length,
        next: rows.length,
      });
      this.apiContactsSig.set(rows);
      this.bindRealtimeSubscriptions(rows);

      const selectedId = this.selectedConversationId();
      const selectedStillExists = rows.some(row => row.convId === selectedId);
      if (!selectedId || !selectedStillExists) {
        this.selectedConversationId.set(rows[0]?.convId || '');
      }
    } catch (error) {
      console.warn('[ChatStore] fallback sync failed', error);
    } finally {
      this.fallbackSyncInFlight = false;
    }
  }

  private areContactsEquivalent(a: ConversationListApiItem[], b: ConversationListApiItem[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      const left = a[i];
      const right = b[i];
      if (left.convId !== right.convId) return false;
      if ((left.lastMessage || '') !== (right.lastMessage || '')) return false;
      if ((left.lastMessageTs || '') !== (right.lastMessageTs || '')) return false;
      if ((left.lastMessageStatus || '') !== (right.lastMessageStatus || '')) return false;
      if ((left.messages?.length || 0) !== (right.messages?.length || 0)) return false;
    }
    return true;
  }

  private bindRealtimeSubscriptions(rows: ConversationListApiItem[]): void {
    console.log('[ChatStore] bindRealtimeSubscriptions', { contacts: rows.length, wsUrl: WS_URL });
    this.realtime.connect(WS_URL);

    const targetConvs = new Set(rows.map(r => r.convId));

    for (const [convId, dispose] of this.liveUnsubscribeByConv.entries()) {
      if (!targetConvs.has(convId)) {
        console.log('[ChatStore] removing realtime subscription', { convId });
        dispose();
        this.liveUnsubscribeByConv.delete(convId);
      }
    }

    for (const convId of targetConvs) {
      if (this.liveUnsubscribeByConv.has(convId)) continue;

      const destination = `/topic/whatsapp/${this.auth.getOrgId()}/${convId}`;
      console.log('[ChatStore] adding realtime subscription', { convId, destination });
      const dispose = this.realtime.subscribe(destination, payload => {
        this.applyRealtimeMessage(convId, payload);
      });

      this.liveUnsubscribeByConv.set(convId, dispose);
    }
  }

  private applyRealtimeMessage(convId: string, payload: unknown): void {
    console.log('[ChatStore] realtime payload received', {
      convId,
      payloadPreview: this.payloadPreview(payload),
    });
    const incoming = this.normalizeIncomingMessage(payload);
    if (!incoming) {
      console.warn('[ChatStore] dropped realtime payload after normalization', {
        convId,
        payloadPreview: this.payloadPreview(payload),
      });
      return;
    }

    this.apiContactsSig.update(rows => {
      const idx = rows.findIndex(r => r.convId === convId);
      if (idx === -1) {
        console.warn('[ChatStore] payload received for unknown conversation', { convId });
        return rows;
      }

      const current = rows[idx];
      const existing = current.messages || [];
      const matchingIdx = existing.findIndex(m => this.isSameMessage(m, incoming));
      if (matchingIdx >= 0) {
        console.log('[ChatStore] merging realtime message into existing message', { convId, matchingIdx });
        const next = rows.slice();
        const mergedMessages = existing.slice();
        const prior = mergedMessages[matchingIdx];
        mergedMessages[matchingIdx] = {
          ...prior,
          ...incoming,
        };
        next[idx] = {
          ...current,
          messages: mergedMessages,
          lastMessage: incoming.text || current.lastMessage,
          lastMessageStatus: incoming.status || current.lastMessageStatus,
          lastMessageTs: incoming.timestamp || current.lastMessageTs,
        };
        return next;
      }

      const next = rows.slice();
      console.log('[ChatStore] appending new realtime message', { convId });
      next[idx] = {
        ...current,
        messages: [...existing, incoming],
        lastMessage: incoming.text || current.lastMessage,
        lastMessageStatus: incoming.status || current.lastMessageStatus,
        lastMessageTs: incoming.timestamp || current.lastMessageTs,
      };
      return next;
    });
  }

  private normalizeIncomingMessage(payload: unknown): ConversationApiMessage | null {
    const raw = payload as {
      direction?: string;
      messageType?: string;
      text?: string | null;
      content?: string | null;
      interactive?: unknown;
      media?: unknown;
      timestamp?: string | number;
      status?: string;
    };

    if (!raw) {
      console.warn('[ChatStore] normalizeIncomingMessage: payload is empty');
      return null;
    }
    if (!raw.direction) {
      console.warn('[ChatStore] normalizeIncomingMessage: missing direction', {
        payloadPreview: this.payloadPreview(payload),
      });
      return null;
    }
    if (!raw.timestamp) {
      console.warn('[ChatStore] normalizeIncomingMessage: missing timestamp', {
        payloadPreview: this.payloadPreview(payload),
      });
      return null;
    }

    return {
      direction: raw.direction.toUpperCase(),
      messageType: raw.messageType || 'TEXT',
      text: raw.text ?? raw.content ?? undefined,
      content: raw.content ?? raw.text ?? undefined,
      interactive: raw.interactive as ConversationApiMessage['interactive'],
      media: raw.media as ConversationApiMessage['media'],
      timestamp: String(raw.timestamp),
      status: raw.status || 'DELIVERED',
    };
  }

  private payloadPreview(payload: unknown): string {
    try {
      const normalized = typeof payload === 'string' ? payload : JSON.stringify(payload);
      if (!normalized) return '';
      return normalized.length > 260 ? `${normalized.slice(0, 260)}...` : normalized;
    } catch {
      return '[unserializable payload]';
    }
  }

  private isSameMessage(existing: ConversationApiMessage, incoming: ConversationApiMessage): boolean {
    const sameDirection = (existing.direction || '').toUpperCase() === (incoming.direction || '').toUpperCase();
    if (!sameDirection) return false;

    const sameType = (existing.messageType || '').toUpperCase() === (incoming.messageType || '').toUpperCase();
    const sameText = (existing.text || existing.content || '') === (incoming.text || incoming.content || '');
    const sameTimestamp = String(existing.timestamp || '') === String(incoming.timestamp || '');

    if (sameDirection && sameType && sameText && sameTimestamp) return true;

    // For optimistic outbound messages, backend/websocket timestamps can differ slightly.
    const outbound = (incoming.direction || '').toUpperCase() === 'OUTBOUND';
    if (!outbound || !sameType || !sameText) return false;

    const existingTs = safeDate(String(existing.timestamp || '')).getTime();
    const incomingTs = safeDate(String(incoming.timestamp || '')).getTime();
    if (Number.isNaN(existingTs) || Number.isNaN(incomingTs)) return false;
    return Math.abs(existingTs - incomingTs) <= 2 * 60 * 1000;
  }
}
